export const LEARNING_CONTENT_SCHEMA_VERSION = 2;
export const LEGACY_LEARNING_CONTENT_SCHEMA_VERSION = 1;
export const LEARNING_CONTENT_DOCUMENT_ID = "main";
export const MAX_LEARNING_CONTENT_BLOCKS = 50;
export const MAX_BLOCK_ID_LENGTH = 128;
export const MAX_HEADING_LENGTH = 200;
export const MAX_TEXT_LENGTH = 10000;
export const MAX_LINK_LABEL_LENGTH = 200;
export const MAX_LINK_URL_LENGTH = 2048;
export const MAX_IMAGE_ALT_TEXT_LENGTH = 500;
export const MAX_IMAGE_CAPTION_LENGTH = 500;
export const MAX_RESOURCE_LABEL_LENGTH = 200;
export const MAX_VIDEO_TITLE_LENGTH = 200;
export const MAX_STORAGE_PATH_LENGTH = 512;
export const MAX_LEARNING_CONTENT_SERIALIZED_BYTES = 500 * 1024;

const DOCUMENT_FIELDS = [
  "schemaVersion",
  "quizId",
  "ownerUid",
  "revision",
  "blocks",
  "createdAt",
  "updatedAt",
];
const HEADING_FIELDS = ["id", "type", "level", "text"];
const TEXT_FIELDS = ["id", "type", "text"];
const LINK_FIELDS = ["id", "type", "label", "url"];
const IMAGE_FIELDS = ["id", "type", "storagePath", "altText", "caption"];
const RESOURCE_FIELDS = ["id", "type", "storagePath", "label"];
const VIDEO_FIELDS = ["id", "type", "title", "url"];
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/i;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/;
const BLOCK_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

export class LearningContentValidationError extends Error {
  constructor(code, message, path = "") {
    super(message);
    this.name = "LearningContentValidationError";
    this.code = code;
    this.path = path;
  }
}

const invalid = (code, message, path = "") => {
  throw new LearningContentValidationError(code, message, path);
};

export const isPlainObject = (value) => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const utf8ByteLength = (value) => {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
};

export const approximateSerializedSizeBytes = (value) => {
  let serialized;
  try {
    serialized = JSON.stringify(value);
  } catch (_error) {
    invalid("not-serializable", "Learning content must be JSON serializable.");
  }
  if (typeof serialized !== "string") {
    invalid("not-serializable", "Learning content must be JSON serializable.");
  }
  return utf8ByteLength(serialized);
};

const assertAllowedFields = (value, allowedFields, path) => {
  const unexpected = Object.keys(value).filter(
    (field) => !allowedFields.includes(field)
  );
  if (unexpected.length > 0) {
    invalid(
      "unexpected-field",
      `Unexpected field "${unexpected[0]}" at ${path}.`,
      path
    );
  }
  const missing = allowedFields.filter(
    (field) => !Object.prototype.hasOwnProperty.call(value, field)
  );
  if (missing.length > 0) {
    invalid(
      "missing-field",
      `Missing field "${missing[0]}" at ${path}.`,
      path
    );
  }
};

const normalizedRequiredText = (value, maximumLength, path) => {
  if (typeof value !== "string") {
    invalid("invalid-text", `${path} must be a string.`, path);
  }
  const normalized = value.trim();
  if (!normalized) {
    invalid("empty-text", `${path} cannot be empty.`, path);
  }
  if (normalized.length > maximumLength) {
    invalid(
      "text-too-long",
      `${path} must be ${maximumLength} characters or fewer.`,
      path
    );
  }
  if (utf8ByteLength(normalized) > maximumLength) {
    invalid(
      "text-too-long",
      `${path} must be ${maximumLength} UTF-8 bytes or fewer.`,
      path
    );
  }
  if (HTML_TAG_PATTERN.test(normalized)) {
    invalid("html-not-supported", `${path} cannot contain HTML.`, path);
  }
  return normalized;
};

const normalizeBlockId = (value, path) => {
  if (typeof value !== "string") {
    invalid("invalid-block-id", `${path} must be a string.`, path);
  }
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.length > MAX_BLOCK_ID_LENGTH ||
    !BLOCK_ID_PATTERN.test(normalized)
  ) {
    invalid(
      "invalid-block-id",
      `${path} must contain 1-${MAX_BLOCK_ID_LENGTH} letters, numbers, underscores, or hyphens.`,
      path
    );
  }
  return normalized;
};

export const normalizeHttpsUrl = (value, path = "url") => {
  if (typeof value !== "string") {
    invalid("invalid-url", `${path} must be a string.`, path);
  }
  if (
    !value ||
    value !== value.trim() ||
    value.length > MAX_LINK_URL_LENGTH ||
    CONTROL_CHARACTER_PATTERN.test(value) ||
    /\s/.test(value)
  ) {
    invalid(
      "invalid-url",
      `${path} must be a valid HTTPS URL without whitespace or control characters.`,
      path
    );
  }

  let parsed;
  try {
    parsed = new URL(value);
  } catch (_error) {
    invalid("invalid-url", `${path} must be a valid HTTPS URL.`, path);
  }

  if (parsed.protocol !== "https:" || !parsed.hostname) {
    invalid("https-required", `${path} must use https:.`, path);
  }
  if (parsed.username || parsed.password) {
    invalid(
      "url-credentials-not-allowed",
      `${path} cannot contain embedded credentials.`,
      path
    );
  }

  const normalized = parsed.href;
  if (normalized.length > MAX_LINK_URL_LENGTH) {
    invalid(
      "url-too-long",
      `${path} must be ${MAX_LINK_URL_LENGTH} characters or fewer.`,
      path
    );
  }
  return normalized;
};

export const parseLearningContentVideoUrl = (value, path = "url") => {
  const normalized = normalizeHttpsUrl(value, path);
  const parsed = new URL(normalized);
  const host = parsed.hostname.toLowerCase();
  const youtubeHosts = ["youtube.com", "www.youtube.com", "m.youtube.com"];
  const embedHosts = [...youtubeHosts, "youtube-nocookie.com", "www.youtube-nocookie.com"];
  let videoId = null;
  if ((host.includes("youtu") && !embedHosts.includes(host) && host !== "youtu.be") ||
      (parsed.port && (embedHosts.includes(host) || host === "youtu.be"))) {
    invalid("unsupported-video-url", `${path} must use a supported YouTube host or a direct HTTPS video file.`, path);
  }
  if (host === "youtu.be") {
    videoId = /^\/([A-Za-z0-9_-]{11})\/?$/.exec(parsed.pathname)?.[1] || null;
  } else if (embedHosts.includes(host)) {
    if (youtubeHosts.includes(host) && parsed.pathname === "/watch") {
      const ids = parsed.searchParams.getAll("v");
      videoId = ids.length === 1 && /^[A-Za-z0-9_-]{11}$/.test(ids[0]) ? ids[0] : null;
    } else {
      videoId = /^\/embed\/([A-Za-z0-9_-]{11})\/?$/.exec(parsed.pathname)?.[1] || null;
    }
  } else if (/\.(mp4|webm|ogv|ogg|m4v|mov)$/i.test(parsed.pathname)) {
    parsed.hash = "";
    return { kind: "direct", url: parsed.href, playbackUrl: parsed.href, fallbackUrl: parsed.href };
  } else {
    invalid("unsupported-video-url", `${path} must be a YouTube video link or a direct HTTPS video file (.mp4, .webm, .ogg, .ogv, .m4v or .mov).`, path);
  }
  if (!videoId) {
    invalid("invalid-youtube-video", `${path} must contain a valid YouTube video ID in a watch, youtu.be or embed link.`, path);
  }
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
  return {
    kind: "youtube", url: embedUrl, playbackUrl: embedUrl,
    fallbackUrl: `https://www.youtube.com/watch?v=${videoId}`,
  };
};

const normalizedOptionalText = (value, maximumLength, path) => {
  if (typeof value !== "string") invalid("invalid-text", `${path} must be a string.`, path);
  const normalized = value.trim();
  if (normalized.length > maximumLength || utf8ByteLength(normalized) > maximumLength) {
    invalid("text-too-long", `${path} must be ${maximumLength} UTF-8 bytes or fewer.`, path);
  }
  if (HTML_TAG_PATTERN.test(normalized)) invalid("html-not-supported", `${path} cannot contain HTML.`, path);
  return normalized;
};

export const parseLearningContentStoragePath = (
  value,
  { expectedOwnerUid, expectedQuizId, expectedBlockId, expectedKind } = {},
  path = "storagePath"
) => {
  if (
    typeof value !== "string" || !value || value !== value.trim() ||
    value.includes("\\") || value.includes("?") || value.includes("#") ||
    value.includes("://") || value.split("/").includes("..") ||
    utf8ByteLength(value) > MAX_STORAGE_PATH_LENGTH
  ) {
    invalid("invalid-storage-path", `${path} must be a canonical learning-content Storage path.`, path);
  }
  const parts = value.split("/");
  if (parts.length !== 5 || parts[0] !== "learning-content") {
    invalid("invalid-storage-path", `${path} must be a canonical learning-content Storage path.`, path);
  }
  const [, ownerUid, quizId, blockId, fileName] = parts;
  validateOwnerUid(ownerUid);
  validateFirestoreDocumentId(quizId, path);
  normalizeBlockId(blockId, path);
  const match = /^([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\.(jpg|png|webp|pdf)$/i.exec(fileName);
  if (!match) invalid("invalid-storage-path", `${path} has an invalid media filename.`, path);
  const extension = match[2].toLowerCase();
  if (expectedOwnerUid !== undefined && ownerUid !== expectedOwnerUid) invalid("media-owner-mismatch", `${path} does not match the content owner.`, path);
  if (expectedQuizId !== undefined && quizId !== expectedQuizId) invalid("media-quiz-mismatch", `${path} does not match the parent test.`, path);
  if (expectedBlockId !== undefined && blockId !== expectedBlockId) invalid("media-block-mismatch", `${path} does not match its block.`, path);
  if (expectedKind === "image" && extension === "pdf") invalid("media-kind-mismatch", `${path} must reference an image.`, path);
  if (expectedKind === "resource" && extension !== "pdf") invalid("media-kind-mismatch", `${path} must reference a PDF.`, path);
  return { ownerUid, quizId, blockId, mediaId: match[1].toLowerCase(), extension };
};

export const normalizeLearningContentBlock = (block, index = 0, options = {}) => {
  const path = `blocks[${index}]`;
  if (!isPlainObject(block)) {
    invalid("invalid-block", `${path} must be a plain object.`, path);
  }
  if (typeof block.type !== "string") {
    invalid("unsupported-block-type", `${path} has no supported type.`, path);
  }

  const id = normalizeBlockId(block.id, `${path}.id`);
  if (block.type === "heading") {
    assertAllowedFields(block, HEADING_FIELDS, path);
    if (block.level !== 2 && block.level !== 3) {
      invalid(
        "invalid-heading-level",
        `${path}.level must be 2 or 3.`,
        `${path}.level`
      );
    }
    return {
      id,
      type: "heading",
      level: block.level,
      text: normalizedRequiredText(
        block.text,
        MAX_HEADING_LENGTH,
        `${path}.text`
      ),
    };
  }

  if (block.type === "text") {
    assertAllowedFields(block, TEXT_FIELDS, path);
    return {
      id,
      type: "text",
      text: normalizedRequiredText(
        block.text,
        MAX_TEXT_LENGTH,
        `${path}.text`
      ),
    };
  }

  if (block.type === "link") {
    assertAllowedFields(block, LINK_FIELDS, path);
    return {
      id,
      type: "link",
      label: normalizedRequiredText(
        block.label,
        MAX_LINK_LABEL_LENGTH,
        `${path}.label`
      ),
      url: normalizeHttpsUrl(block.url, `${path}.url`),
    };
  }

  if (block.type === "video") {
    assertAllowedFields(block, VIDEO_FIELDS, path);
    return {
      id,
      type: "video",
      title: normalizedOptionalText(block.title, MAX_VIDEO_TITLE_LENGTH, `${path}.title`),
      url: parseLearningContentVideoUrl(block.url, `${path}.url`).url,
    };
  }

  if (block.type === "image") {
    assertAllowedFields(block, IMAGE_FIELDS, path);
    const hasPendingFile = options.allowPendingMedia === true && Boolean(options.mediaDrafts?.[id]?.file);
    if (!block.storagePath && !hasPendingFile) {
      invalid("media-file-required", `${path} requires an image file.`, `${path}.storagePath`);
    }
    if (block.storagePath) {
      parseLearningContentStoragePath(block.storagePath, {
        expectedOwnerUid: options.expectedOwnerUid,
        expectedQuizId: options.expectedQuizId,
        expectedBlockId: id,
        expectedKind: "image",
      }, `${path}.storagePath`);
    }
    return {
      id,
      type: "image",
      storagePath: block.storagePath,
      altText: normalizedRequiredText(block.altText, MAX_IMAGE_ALT_TEXT_LENGTH, `${path}.altText`),
      caption: normalizedOptionalText(block.caption, MAX_IMAGE_CAPTION_LENGTH, `${path}.caption`),
    };
  }

  if (block.type === "resource") {
    assertAllowedFields(block, RESOURCE_FIELDS, path);
    const hasPendingFile = options.allowPendingMedia === true && Boolean(options.mediaDrafts?.[id]?.file);
    if (!block.storagePath && !hasPendingFile) {
      invalid("media-file-required", `${path} requires a PDF file.`, `${path}.storagePath`);
    }
    if (block.storagePath) {
      parseLearningContentStoragePath(block.storagePath, {
        expectedOwnerUid: options.expectedOwnerUid,
        expectedQuizId: options.expectedQuizId,
        expectedBlockId: id,
        expectedKind: "resource",
      }, `${path}.storagePath`);
    }
    return {
      id,
      type: "resource",
      storagePath: block.storagePath,
      label: normalizedRequiredText(block.label, MAX_RESOURCE_LABEL_LENGTH, `${path}.label`),
    };
  }

  invalid(
    "unsupported-block-type",
    `${path}.type must be heading, text, link, image, resource, or video.`,
    `${path}.type`
  );
};

export const normalizeLearningContentBlocks = (blocks, options = {}) => {
  if (!Array.isArray(blocks)) {
    invalid("invalid-blocks", "blocks must be an array.", "blocks");
  }
  if (blocks.length === 0) {
    invalid(
      "empty-content",
      "Learning content must contain at least one meaningful block.",
      "blocks"
    );
  }
  if (blocks.length > MAX_LEARNING_CONTENT_BLOCKS) {
    invalid(
      "too-many-blocks",
      `Learning content can contain at most ${MAX_LEARNING_CONTENT_BLOCKS} blocks.`,
      "blocks"
    );
  }

  const normalized = blocks.map((block, index) => normalizeLearningContentBlock(block, index, options));
  const ids = new Set();
  normalized.forEach((block, index) => {
    if (ids.has(block.id)) {
      invalid(
        "duplicate-block-id",
        `Block id "${block.id}" is duplicated.`,
        `blocks[${index}].id`
      );
    }
    ids.add(block.id);
  });
  const mediaPaths = new Set();
  normalized.forEach((block, index) => {
    if ((block.type !== "image" && block.type !== "resource") || !block.storagePath) return;
    const mediaId = parseLearningContentStoragePath(block.storagePath).mediaId;
    if (mediaPaths.has(mediaId)) {
      invalid("duplicate-media-path", "A media object cannot be used by more than one block.", `blocks[${index}].storagePath`);
    }
    mediaPaths.add(mediaId);
  });
  return normalized;
};

export const normalizeLearningContentDraftBlocks = (blocks, mediaDrafts = {}) =>
  normalizeLearningContentBlocks(blocks, { allowPendingMedia: true, mediaDrafts });

const validateFirestoreDocumentId = (value, path) => {
  if (typeof value !== "string") {
    invalid("invalid-document-id", `${path} must be a string.`, path);
  }
  const normalized = value.trim();
  if (
    !normalized ||
    normalized !== value ||
    normalized.includes("/") ||
    normalized === "." ||
    normalized === ".." ||
    /^__.*__$/.test(normalized) ||
    CONTROL_CHARACTER_PATTERN.test(normalized) ||
    utf8ByteLength(normalized) > 1500
  ) {
    invalid("invalid-document-id", `${path} is not a valid Firestore document ID.`, path);
  }
  return normalized;
};

const validateOwnerUid = (value) => {
  if (
    typeof value !== "string" ||
    !value.trim() ||
    value !== value.trim() ||
    value.includes("/") ||
    CONTROL_CHARACTER_PATTERN.test(value) ||
    utf8ByteLength(value) > 128
  ) {
    invalid("invalid-owner", "ownerUid must be a valid non-empty UID.", "ownerUid");
  }
  return value;
};

const isTimestampLike = (value) => {
  if (value instanceof Date) return Number.isFinite(value.getTime());
  return Boolean(
    value &&
      typeof value === "object" &&
      (typeof value.toDate === "function" ||
        typeof value.toMillis === "function" ||
        (Number.isInteger(value.seconds) && Number.isInteger(value.nanoseconds)))
  );
};

const assertSerializedSize = (documentData) => {
  const size = approximateSerializedSizeBytes(documentData);
  if (size > MAX_LEARNING_CONTENT_SERIALIZED_BYTES) {
    invalid(
      "content-too-large",
      `Learning content must be no larger than ${MAX_LEARNING_CONTENT_SERIALIZED_BYTES} serialized bytes.`
    );
  }
  return size;
};

export const validateLearningContentDocument = (
  value,
  { expectedQuizId, expectedOwnerUid } = {}
) => {
  if (!isPlainObject(value)) {
    invalid("invalid-document", "Learning content must be a plain object.");
  }
  assertAllowedFields(value, DOCUMENT_FIELDS, "learningContent");
  if (![LEGACY_LEARNING_CONTENT_SCHEMA_VERSION, LEARNING_CONTENT_SCHEMA_VERSION].includes(value.schemaVersion)) {
    invalid("invalid-schema-version", "schemaVersion must equal 1 or 2.", "schemaVersion");
  }

  const quizId = validateFirestoreDocumentId(value.quizId, "quizId");
  const ownerUid = validateOwnerUid(value.ownerUid);
  if (expectedQuizId !== undefined && quizId !== expectedQuizId) {
    invalid("quiz-id-mismatch", "quizId does not match the parent test.", "quizId");
  }
  if (expectedOwnerUid !== undefined && ownerUid !== expectedOwnerUid) {
    invalid("owner-mismatch", "ownerUid does not match the test owner.", "ownerUid");
  }
  if (!Number.isInteger(value.revision) || value.revision < 1) {
    invalid("invalid-revision", "revision must be a positive integer.", "revision");
  }
  if (!isTimestampLike(value.createdAt) || !isTimestampLike(value.updatedAt)) {
    invalid(
      "invalid-timestamp",
      "createdAt and updatedAt must be Firestore-compatible timestamps."
    );
  }

  const normalized = {
    schemaVersion: value.schemaVersion,
    quizId,
    ownerUid,
    revision: value.revision,
    blocks: normalizeLearningContentBlocks(value.blocks, { expectedQuizId: quizId, expectedOwnerUid: ownerUid }),
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
  };
  if (value.schemaVersion === LEGACY_LEARNING_CONTENT_SCHEMA_VERSION && normalized.blocks.some((block) => block.type === "image" || block.type === "resource" || block.type === "video")) {
    invalid("media-requires-schema-v2", "Media blocks require schemaVersion 2.", "schemaVersion");
  }
  assertSerializedSize(normalized);
  return normalized;
};
